import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { lazy, Suspense, useEffect } from 'react';
import { useAuthStore } from './stores/authStore';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import NewMeetingPage from './pages/NewMeetingPage';
import MeetingHistoryPage from './pages/MeetingHistoryPage';
import MeetingDetailPage from './pages/MeetingDetailPage';
import AddNotePage from './pages/AddNotePage';
import AdminPage from './pages/AdminPage';
import AdminPromptsPage from './pages/AdminPromptsPage';
import EmailSettingsPage from './pages/EmailSettingsPage';
import ApiUsageAnalyticsPage from './pages/ApiUsageAnalyticsPage';

// Lazy-loaded landing page (separate chunk for unauthenticated visitors)
const LandingPage = lazy(() => import('./pages/LandingPage'));

// Components
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import { ScrollToTop } from './components/ScrollToTop';

// Landing gate: shows landing for guests, redirects logged-in users to dashboard
const LandingGate = () => {
  const { token } = useAuthStore();
  if (token) return <Navigate to="/dashboard" replace />;
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <LandingPage />
    </Suspense>
  );
};

function App() {
  const { verifyToken, token } = useAuthStore();

  useEffect(() => {
    // Verify token on app load
    if (token) {
      verifyToken();
    }
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(222 47% 15%)',
            color: 'hsl(210 40% 98%)',
            border: '1px solid hsl(217 33% 17%)',
          },
          success: {
            iconTheme: {
              primary: 'hsl(142 76% 36%)',
              secondary: 'hsl(222 47% 15%)',
            },
          },
          error: {
            iconTheme: {
              primary: 'hsl(0 84% 60%)',
              secondary: 'hsl(222 47% 15%)',
            },
          },
        }}
      />

      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingGate />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes — layout route (pathless) wraps all authenticated pages */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/new-meeting" element={<NewMeetingPage />} />
          <Route path="/history" element={<MeetingHistoryPage />} />
          <Route path="/meeting/:id" element={<MeetingDetailPage />} />
          <Route path="/meeting/:id/add-note" element={<AddNotePage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/prompts"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPromptsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-settings"
            element={
              <ProtectedRoute requireAdmin>
                <EmailSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/api-usage"
            element={
              <ProtectedRoute requireAdmin>
                <ApiUsageAnalyticsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
